import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import ModulePageHeading from '../../components/ModulePageHeading';
import { resolveNetworkNav } from '../../navigation/networkNavigation';

const ORG_TYPE_LABEL = {
  AGENT: 'Our agent company',
  PARTNER: 'Partner (Form 12A)',
  IQP_COMPANY: 'IQP company (Council register)',
  ENGINEERING_FIRM: 'Engineering firm',
};

const ORG_TYPES = ['PARTNER', 'IQP_COMPANY', 'ENGINEERING_FIRM', 'AGENT'];

function emptyForm() {
  return {
    name: '',
    org_type: 'PARTNER',
    contact_name: '',
    partner_iqp_name: '',
    license_number: '',
    email: '',
    phone: '',
    address: '',
    partner_ss_codes: '',
    notes: '',
  };
}

function orgToForm(org) {
  return {
    name: org.name || '',
    org_type: org.org_type || 'PARTNER',
    contact_name: org.contact_name || '',
    partner_iqp_name: org.partner_iqp_name || '',
    license_number: org.license_number || '',
    email: org.email || '',
    phone: org.phone || '',
    address: org.address || '',
    partner_ss_codes: Array.isArray(org.partner_ss_codes)
      ? org.partner_ss_codes.join(', ')
      : org.partner_ss_codes || '',
    notes: org.notes || '',
  };
}

export default function OrganizationDetail() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(searchParams.get('edit') === '1');
  const [form, setForm] = useState(emptyForm());

  useEffect(() => {
    if (searchParams.get('edit') === '1') setEditing(true);
  }, [searchParams]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['service-network', 'org-buildings', orgId],
    queryFn: async () => (await api.get(`/service-network/organizations/${orgId}/buildings`)).data?.data,
    enabled: !!orgId,
  });

  const org = data?.organization;
  const buildings = data?.buildings ?? [];

  useEffect(() => {
    if (org) setForm(orgToForm(org));
  }, [org]);

  const saveMutation = useMutation({
    mutationFn: async (payload) => {
      const body = {
        ...payload,
        partner_ss_codes: String(payload.partner_ss_codes || '')
          .split(/[,;]+/)
          .map((s) => s.trim())
          .filter(Boolean),
        iqp_name: payload.partner_iqp_name,
      };
      return (await api.patch(`/service-network/organizations/${orgId}`, body)).data?.data;
    },
    onSuccess: () => {
      toast.success('Company saved');
      setEditing(false);
      if (searchParams.get('edit') === '1') {
        const next = new URLSearchParams(searchParams);
        next.delete('edit');
        setSearchParams(next, { replace: true });
      }
      queryClient.invalidateQueries({ queryKey: ['service-network'] });
      queryClient.invalidateQueries({ queryKey: ['service-network', 'org-buildings', orgId] });
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Save failed'),
  });

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const ssEntries = Object.entries(org?.iqps_by_ss || {}).filter(([, list]) => list.length > 0);

  return (
    <div>
      <ModulePageHeading
        resolveNav={resolveNetworkNav}
        title={org?.name || 'Company'}
        infoTitle="Partner company"
        infoChildren={
          <p>
            Supplier company that inspects SS on our buildings. Edit contact and licence details here. Use{' '}
            <strong>Register our partner</strong> to open the Our Partner form with this company&apos;s details
            pre-filled. Buildings linked via Service mandates are listed below.
          </p>
        }
        actions={
          org ? (
            editing ? (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setForm(orgToForm(org));
                    setEditing(false);
                  }}
                  className="px-3 py-2 text-sm border rounded-lg hover:bg-gray-50"
                  disabled={saveMutation.isPending}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => saveMutation.mutate(form)}
                  disabled={saveMutation.isPending || !form.name.trim()}
                  className="px-3 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  {saveMutation.isPending ? 'Saving…' : 'Save'}
                </button>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {org.partner_company_id ? (
                  <button
                    type="button"
                    onClick={() => navigate(`/network/partners/${org.partner_company_id}`)}
                    className="px-3 py-2 text-sm font-semibold rounded-lg border border-navy-200 text-navy-900 hover:bg-navy-50"
                  >
                    Open Our Partner
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() =>
                      navigate(
                        `/network/partners/new?fromOrg=${orgId}&return=${encodeURIComponent(`/network/org/${orgId}`)}`
                      )
                    }
                    className="px-3 py-2 text-sm font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-700"
                  >
                    Register our partner
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  className="px-3 py-2 text-sm border border-primary-200 text-primary-800 rounded-lg hover:bg-primary-50"
                >
                  Edit company
                </button>
              </div>
            )
          ) : null
        }
      />

      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}
      {error && <p className="text-sm text-red-600">Could not load company.</p>}

      {org && (
        <div className="space-y-6">
          <section className="bg-card rounded-lg border border-gray-200 p-5">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Company details</h2>

            {editing ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <label className="block sm:col-span-2">
                  <span className="text-gray-600">Company name *</span>
                  <input
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={form.name}
                    onChange={(e) => setField('name', e.target.value)}
                  />
                </label>
                <label className="block">
                  <span className="text-gray-600">Type</span>
                  <select
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={form.org_type}
                    onChange={(e) => setField('org_type', e.target.value)}
                  >
                    {ORG_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {ORG_TYPE_LABEL[t] || t}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="text-gray-600">Licence number</span>
                  <input
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={form.license_number}
                    onChange={(e) => setField('license_number', e.target.value)}
                  />
                </label>
                <label className="block">
                  <span className="text-gray-600">Contact name</span>
                  <input
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={form.contact_name}
                    onChange={(e) => setField('contact_name', e.target.value)}
                  />
                </label>
                <label className="block">
                  <span className="text-gray-600">IQP name (Form 12A)</span>
                  <input
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={form.partner_iqp_name}
                    onChange={(e) => setField('partner_iqp_name', e.target.value)}
                  />
                </label>
                <label className="block">
                  <span className="text-gray-600">Email</span>
                  <input
                    type="email"
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={form.email}
                    onChange={(e) => setField('email', e.target.value)}
                  />
                </label>
                <label className="block">
                  <span className="text-gray-600">Phone</span>
                  <input
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={form.phone}
                    onChange={(e) => setField('phone', e.target.value)}
                  />
                </label>
                <label className="block sm:col-span-2">
                  <span className="text-gray-600">Address</span>
                  <textarea
                    rows={2}
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={form.address}
                    onChange={(e) => setField('address', e.target.value)}
                  />
                </label>
                <label className="block sm:col-span-2">
                  <span className="text-gray-600">SS codes (comma-separated)</span>
                  <input
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    placeholder="e.g. SS 9, SS 4, SS 15.2"
                    value={form.partner_ss_codes}
                    onChange={(e) => setField('partner_ss_codes', e.target.value)}
                  />
                </label>
                <label className="block sm:col-span-2">
                  <span className="text-gray-600">Notes</span>
                  <textarea
                    rows={3}
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={form.notes}
                    onChange={(e) => setField('notes', e.target.value)}
                  />
                </label>
              </div>
            ) : (
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <div>
                  <dt className="text-gray-500">Type</dt>
                  <dd className="font-medium text-gray-900">{ORG_TYPE_LABEL[org.org_type] || org.org_type}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Licence</dt>
                  <dd className="font-medium text-gray-900">{org.license_number || '—'}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Contact</dt>
                  <dd className="font-medium text-gray-900">{org.contact_name || '—'}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">IQP (Form 12A)</dt>
                  <dd className="font-medium text-gray-900">{org.partner_iqp_name || '—'}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Email</dt>
                  <dd className="font-medium text-gray-900 break-all">{org.email || '—'}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Phone</dt>
                  <dd className="font-medium text-gray-900">{org.phone || '—'}</dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-gray-500">Address</dt>
                  <dd className="font-medium text-gray-900 whitespace-pre-wrap">{org.address || '—'}</dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-gray-500">SS codes (partner record)</dt>
                  <dd className="font-medium text-gray-900">
                    {org.partner_ss_codes?.length ? org.partner_ss_codes.join(', ') : '—'}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">Linked IQPs</dt>
                  <dd className="font-medium text-gray-900">{org.iqp_count ?? 0}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Linked engineers</dt>
                  <dd className="font-medium text-gray-900">{org.expert_count ?? 0}</dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-gray-500">Notes</dt>
                  <dd className="text-gray-800 whitespace-pre-wrap">{org.notes || '—'}</dd>
                </div>
              </dl>
            )}
          </section>

          {ssEntries.length > 0 && (
            <section className="bg-card rounded-lg border border-gray-200 p-5">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">IQPs by SS</h2>
              <div className="space-y-3">
                {ssEntries.map(([ssCode, iqps]) => (
                  <div key={ssCode}>
                    <p className="text-sm font-medium text-primary-800 mb-1">{ssCode}</p>
                    <ul className="text-sm text-gray-700 space-y-1">
                      {iqps.map((iqp, idx) => (
                        <li key={iqp.id || `${ssCode}-${idx}`}>
                          {iqp.person_name}
                          {iqp.email ? ` · ${iqp.email}` : ''}
                          {iqp.phone ? ` · ${iqp.phone}` : ''}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </section>
          )}

          {org.affiliated_experts?.length > 0 && (
            <section className="bg-card rounded-lg border border-gray-200 p-5">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Linked engineers</h2>
              <ul className="text-sm text-gray-700 space-y-1">
                {org.affiliated_experts.map((ex) => (
                  <li key={ex.id}>
                    {ex.name}
                    {ex.expert_group ? ` (${ex.expert_group})` : ''}
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="bg-card rounded-lg border border-gray-200">
            <div className="px-5 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">
                Buildings they inspect for us ({buildings.length})
              </h2>
              <p className="text-xs text-gray-500 mt-1">
                From Service mandates and SS Default IQP (external). Open a building for all-SS
                history, or an SS for a date list.
              </p>
            </div>
            {buildings.length === 0 ? (
              <p className="p-6 text-sm text-gray-500 text-center">
                No buildings linked yet. Add under Building → Service mandates, or set Default IQP
                on an SS to this company.
              </p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {buildings.map((b) => (
                  <li key={b.building_id} className="p-4 sm:p-5">
                    <button
                      type="button"
                      onClick={() => navigate(`/network/org/${orgId}/buildings/${b.building_id}`)}
                      className="w-full text-left flex items-start gap-3 group"
                    >
                      <Building2 className="w-5 h-5 text-navy-800 shrink-0 mt-0.5" />
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-gray-900 group-hover:text-navy-800">{b.building_name}</p>
                        {b.address && <p className="text-sm text-gray-500 truncate">{b.address}</p>}
                      </div>
                    </button>
                    {b.ss_list?.length > 0 && (
                      <div className="mt-3 ml-8 flex flex-wrap gap-2">
                        {b.ss_list.map((s) => (
                          <Link
                            key={s.ss_code}
                            to={`/network/org/${orgId}/buildings/${b.building_id}/ss/${encodeURIComponent(s.ss_code)}`}
                            className="inline-flex items-center px-2.5 py-1 rounded-md border border-navy-200 bg-navy-900/5 text-xs font-medium text-navy-900 hover:bg-navy-900 hover:text-white hover:border-navy-900 transition-colors"
                          >
                            {s.ss_code}
                            {s.ss_name && s.ss_name !== s.ss_code ? ` · ${s.ss_name}` : ''}
                          </Link>
                        ))}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
